<template>
  <div class="tools-view p-6 space-y-8 max-w-7xl mx-auto">
    <!-- Header Section -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">MCP Tools</h1>
        <p class="mt-2 text-gray-600 dark:text-gray-400">
          Discover and execute powerful AI-powered tools for your workflows
        </p>
      </div>
      <div class="mt-4 lg:mt-0 flex items-center space-x-4">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search tools..."
            class="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          />
          <svg
            class="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <select
          v-model="selectedCategory"
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">All Categories</option>
          <option value="search">Search</option>
          <option value="calculation">Calculator</option>
          <option value="communication">Communication</option>
          <option value="document">Document</option>
          <option value="ai">AI</option>
          <option value="utility">Utility</option>
        </select>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <nav class="-mb-px flex space-x-8" aria-label="Tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="[
            activeTab === tab.id
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
            'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
          ]"
          @click="activeTab = tab.id"
        >
          {{ tab.name }}
          <span
            v-if="tab.count !== undefined"
            class="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 py-0.5 px-2 rounded-full text-xs"
          >
            {{ tab.count }}
          </span>
        </button>
      </nav>
    </div>

    <!-- Content -->
    <div class="space-y-6">
      <!-- Tools Grid -->
      <div v-if="activeTab === 'browse'" class="space-y-6">
        <!-- Loading State -->
        <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="i in 6" :key="i" class="skeleton-card">
            <div class="skeleton h-6 w-3/4 mb-3" />
            <div class="skeleton h-4 w-full mb-2" />
            <div class="skeleton h-4 w-2/3" />
          </div>
        </div>

        <!-- Tools Grid -->
        <div
          v-else-if="filteredTools.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <ToolCard
            v-for="tool in filteredTools"
            :key="tool.name"
            :tool="tool"
            @execute="openToolDetail"
            @favorite="toggleFavorite"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No tools found</h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      </div>

      <!-- Favorites -->
      <div v-if="activeTab === 'favorites'" class="space-y-6">
        <div
          v-if="favoriteTools.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <ToolCard
            v-for="tool in favoriteTools"
            :key="tool.name"
            :tool="tool"
            :is-favorite="true"
            @execute="openToolDetail"
            @favorite="toggleFavorite"
          />
        </div>
        <div v-else class="text-center py-12">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No favorite tools
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Mark tools as favorites to see them here.
          </p>
        </div>
      </div>

      <!-- History -->
      <div v-if="activeTab === 'history'" class="space-y-6">
        <div v-if="recentExecutions.length > 0" class="space-y-4">
          <ExecutionHistoryCard
            v-for="execution in recentExecutions"
            :key="execution.id"
            :execution="execution"
            @view="viewExecution"
            @retry="retryExecution"
          />
        </div>
        <div v-else class="text-center py-12">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No execution history
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Execute some tools to see your history here.
          </p>
        </div>
      </div>

      <!-- Templates -->
      <div v-if="activeTab === 'templates'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Saved Templates</h2>
          <button class="btn-primary" @click="showCreateTemplate = true">Create Template</button>
        </div>

        <div
          v-if="templates.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <TemplateCard
            v-for="template in templates"
            :key="template.id"
            :template="template"
            @use="useTemplate"
            @edit="editTemplate"
            @delete="deleteTemplate"
          />
        </div>
        <div v-else class="text-center py-12">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No templates saved
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Save tool configurations as templates for quick reuse.
          </p>
        </div>
      </div>
    </div>

    <!-- Create Template Modal -->
    <CreateTemplateModal
      v-if="showCreateTemplate"
      @close="showCreateTemplate = false"
      @created="onTemplateCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { mcpApi } from '@/services/mcp-api';
import { storageService } from '@/services/storage';
import type { McpTool, ToolExecution } from '@/types/mcp';

// Components
import ToolCard from '@/components/tools/ToolCard.vue';
import ExecutionHistoryCard from '@/components/tools/ExecutionHistoryCard.vue';
import TemplateCard from '@/components/tools/TemplateCard.vue';
import CreateTemplateModal from '@/components/tools/CreateTemplateModal.vue';

const router = useRouter();
const route = useRoute();

// State
const isLoading = ref(true);
const searchQuery = ref('');
const selectedCategory = ref('');
const activeTab = ref('browse');
const showCreateTemplate = ref(false);

// Data
const tools = ref<McpTool[]>([]);
const recentExecutions = ref<ToolExecution[]>([]);
const templates = ref<any[]>([]);
const favoriteToolNames = ref<string[]>([]);

// Tabs configuration
const tabs = computed(() => [
  { id: 'browse', name: 'Browse', count: tools.value.length },
  { id: 'favorites', name: 'Favorites', count: favoriteTools.value.length },
  { id: 'history', name: 'History', count: recentExecutions.value.length },
  { id: 'templates', name: 'Templates', count: templates.value.length },
]);

// Computed
const filteredTools = computed(() => {
  let filtered = tools.value;

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      tool =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(tool => tool.category === selectedCategory.value);
  }

  return filtered;
});

const favoriteTools = computed(() => {
  return tools.value.filter(tool => favoriteToolNames.value.includes(tool.name));
});

// Methods
const loadTools = async () => {
  try {
    isLoading.value = true;
    const registry = await mcpApi.getToolRegistry();
    tools.value = Object.values(registry.tools || {});
  } catch (error) {
    console.error('Failed to load tools:', error);
    window.notifications?.error('Failed to load tools');
  } finally {
    isLoading.value = false;
  }
};

const loadExecutionHistory = async () => {
  try {
    const executions = await mcpApi.getToolExecutionHistory(50);
    recentExecutions.value = executions;
  } catch (error) {
    console.error('Failed to load execution history:', error);
  }
};

const loadTemplates = async () => {
  try {
    const userTemplates = await mcpApi.getUserTemplates();
    templates.value = userTemplates;
  } catch (error) {
    console.error('Failed to load templates:', error);
  }
};

const loadFavorites = () => {
  favoriteToolNames.value = storageService.getFavoriteTools();
};

const saveFavorites = () => {
  storageService.setFavoriteTools(favoriteToolNames.value);
};

const toggleFavorite = (toolName: string) => {
  const index = favoriteToolNames.value.indexOf(toolName);
  if (index > -1) {
    favoriteToolNames.value.splice(index, 1);
    window.notifications?.info(`Removed ${formatToolName(toolName)} from favorites`);
  } else {
    favoriteToolNames.value.push(toolName);
    window.notifications?.success(`Added ${formatToolName(toolName)} to favorites`);
  }
  saveFavorites();
};

const openToolDetail = (tool: McpTool) => {
  router.push({ name: 'tool-detail', params: { toolName: tool.name } });
};

const viewExecution = (execution: ToolExecution) => {
  router.push({
    name: 'tool-detail',
    params: { toolName: execution.toolName },
    query: { execution: execution.id },
  });
};

const retryExecution = async (execution: ToolExecution) => {
  try {
    const result = await mcpApi.executeTool({
      toolName: execution.toolName,
      arguments: execution.arguments,
    });

    if (result.success) {
      window.notifications?.success('Tool executed successfully');
      await loadExecutionHistory();
    } else {
      window.notifications?.error(result.error?.message || 'Execution failed');
    }
  } catch (error) {
    console.error('Retry execution error:', error);
    window.notifications?.error('Failed to retry execution');
  }
};

const useTemplate = (template: any) => {
  router.push({
    name: 'tool-detail',
    params: { toolName: template.toolName },
    query: { template: template.id },
  });
};

const editTemplate = (template: any) => {
  // Open edit modal or navigate to edit page
  console.log('Edit template:', template);
};

const deleteTemplate = async (template: any) => {
  if (confirm('Are you sure you want to delete this template?')) {
    try {
      // Implement template deletion
      console.log('Delete template:', template);
      await loadTemplates();
      window.notifications?.success('Template deleted successfully');
    } catch (error) {
      console.error('Delete template error:', error);
      window.notifications?.error('Failed to delete template');
    }
  }
};

const onTemplateCreated = () => {
  showCreateTemplate.value = false;
  loadTemplates();
  window.notifications?.success('Template created successfully');
};

const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Watch for route changes
watch(
  () => route.query.tab,
  tab => {
    if (tab && typeof tab === 'string') {
      activeTab.value = tab;
    }
  },
  { immediate: true }
);

// Watch for active tab changes
watch(activeTab, newTab => {
  router.replace({ query: { ...route.query, tab: newTab } });
});

// Lifecycle
onMounted(async () => {
  loadFavorites();
  await Promise.allSettled([loadTools(), loadExecutionHistory(), loadTemplates()]);

  // Set initial tab from query
  const tabFromQuery = route.query.tab as string;
  if (tabFromQuery && tabs.value.some(tab => tab.id === tabFromQuery)) {
    activeTab.value = tabFromQuery;
  }
});

// Set page title
document.title = 'Tools | MCP Tools Collection';
</script>

<style scoped>
.tools-view {
  min-height: calc(100vh - 4rem);
}

.skeleton-card {
  @apply bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6;
}

.skeleton {
  @apply bg-gray-200 dark:bg-gray-700 animate-pulse rounded;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .tools-view {
    padding: 1rem;
  }

  .grid {
    grid-template-columns: 1fr;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}
</style>
